/**
 * Job Completion Report (20.2) — sortable table, summary row, margin chart.
 */

import { useCallback, useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Line, ComposedChart } from 'recharts';
import { fetchJobCompletionReport, exportJobCompletionCSV, exportJobCompletionPDF } from '../../api/reports';
import { LoadingSpinner, EmptyState } from '../common';
import ReportShell, { DateRangeFilter } from './ReportShell';

function fmtCurrency(v) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v || 0);
}

export default function JobCompletionReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [exporting, setExporting] = useState(null);

  const params = { date_from: dateFrom || undefined, date_to: dateTo || undefined };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await fetchJobCompletionReport(params);
      setData(d);
    } catch { setData(null); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  async function handleExport(type) {
    setExporting(type);
    try {
      if (type === 'csv') await exportJobCompletionCSV(params);
      else await exportJobCompletionPDF(params);
    } catch { /* ignore */ }
    finally { setExporting(null); }
  }

  const rows = data?.rows || [];
  const summary = data?.summary || {};

  // Chart data — last 20 rows
  const chartData = rows.slice(-20).map((r) => ({
    name: `#${r.job_number}`,
    revenue: parseFloat(r.sell_price) || 0,
    cost: parseFloat(r.true_cost) || 0,
    margin: parseFloat(r.margin_pct) || 0,
  }));

  return (
    <ReportShell
      title="Job Completion"
      exporting={exporting}
      onExportCSV={() => handleExport('csv')}
      onExportPDF={() => handleExport('pdf')}
      filters={
        <>
          <DateRangeFilter dateFrom={dateFrom} dateTo={dateTo} onChangeFrom={setDateFrom} onChangeTo={setDateTo} />
          <button onClick={load} className="px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700">Run</button>
        </>
      }
    >
      {loading ? <div className="flex justify-center py-10"><LoadingSpinner /></div> : rows.length === 0 ? (
        <EmptyState title="No completed jobs" description="Adjust date filters or archive completed jobs." />
      ) : (
        <>
          {/* Table */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wider">
                  <th className="px-3 py-2">Job #</th>
                  <th className="px-3 py-2">Customer</th>
                  <th className="px-3 py-2">Item</th>
                  <th className="px-3 py-2 text-right">Ext Sell Price</th>
                  <th className="px-3 py-2 text-right">True Cost</th>
                  <th className="px-3 py-2 text-right">Margin</th>
                  <th className="px-3 py-2 text-right">Margin %</th>
                  <th className="px-3 py-2 text-right">Hours</th>
                  <th className="px-3 py-2 text-right">Variance</th>
                  <th className="px-3 py-2 text-right">Days</th>
                  <th className="px-3 py-2">Completed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((r) => (
                  <tr key={r.job_id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium">{r.job_number}</td>
                    <td className="px-3 py-2 text-gray-600">{r.customer_name}</td>
                    <td className="px-3 py-2 text-gray-500">{r.parent_item_no}</td>
                    <td className="px-3 py-2 text-right">{fmtCurrency(r.sell_price)}</td>
                    <td className="px-3 py-2 text-right">{fmtCurrency(r.true_cost)}</td>
                    <td className="px-3 py-2 text-right">{fmtCurrency(r.margin)}</td>
                    <td className="px-3 py-2 text-right">
                      <span className={parseFloat(r.margin_pct) > 20 ? 'text-green-600' : parseFloat(r.margin_pct) > 0 ? 'text-yellow-600' : 'text-red-600'}>
                        {r.margin_pct}%
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">{parseFloat(r.actual_hours).toFixed(1)}</td>
                    <td className="px-3 py-2 text-right">
                      {r.labor_variance != null && (
                        <span className={parseFloat(r.labor_variance) > 0 ? 'text-red-600' : 'text-green-600'}>
                          {parseFloat(r.labor_variance) > 0 ? '+' : ''}{parseFloat(r.labor_variance).toFixed(1)}h
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-500">{r.days_to_complete ?? '—'}</td>
                    <td className="px-3 py-2 text-gray-400 text-xs">{r.completion_date}</td>
                  </tr>
                ))}
                {/* Summary row */}
                <tr className="bg-gray-50 font-semibold">
                  <td className="px-3 py-2" colSpan={3}>{summary.job_count} jobs</td>
                  <td className="px-3 py-2 text-right">{fmtCurrency(summary.total_revenue)}</td>
                  <td className="px-3 py-2 text-right">{fmtCurrency(summary.total_cost)}</td>
                  <td className="px-3 py-2 text-right">{fmtCurrency(summary.total_margin)}</td>
                  <td className="px-3 py-2" colSpan={2} />
                  <td className="px-3 py-2 text-right">avg {parseFloat(summary.avg_labor_hours || 0).toFixed(1)}h</td>
                  <td className="px-3 py-2" colSpan={2} />
                </tr>
              </tbody>
            </table>
          </div>

          {/* Chart */}
          {chartData.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h3 className="text-sm font-medium text-foreground mb-3">Revenue vs Cost by Job</h3>
              <ResponsiveContainer width="100%" height={250}>
                <ComposedChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} unit="%" />
                  <Tooltip />
                  <Bar yAxisId="left" dataKey="revenue" fill="#3b82f6" name="Revenue" />
                  <Bar yAxisId="left" dataKey="cost" fill="#ef4444" name="Cost" />
                  <Line yAxisId="right" type="monotone" dataKey="margin" stroke="#10b981" name="Margin %" strokeWidth={2} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}
    </ReportShell>
  );
}
